import { useEffect, useState } from "react";
import {
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  Modal,
  Typography,
  Avatar,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { ChannelService } from "../../services/channelService";
import { Box } from "@mui/system";
import { Cancel, CloudUpload, Delete, Save } from "@mui/icons-material";
import ConfirmDeleteDialog from "./dialogs/ConfirmDeleteDialog";
import { useDropzone } from "react-dropzone";

interface EditChannelProps {
  open: boolean;
  onClose: () => void;
  channelId: number;
  initialName: string;
  initialDescription: string;
  initialIsPublic: boolean;
  initialRequiresInvite: boolean;
  initialWhatsappLink?: string;
  initialImage?: string;

  onUpdated: () => void;
  fetchChannels?: () => Promise<void>;
  fetchPublicChannels?: () => Promise<void>;
  setSelectedTab?: (tab: "channels" | "global") => void;
  setSelectedChannel?: (channel: any) => void;
}

export default function EditChannel({
  open,
  onClose,
  channelId,
  initialName,
  initialDescription,
  initialIsPublic,
  initialRequiresInvite,
  initialImage,
  initialWhatsappLink = "",
  onUpdated,
  fetchChannels = async () => {},
  fetchPublicChannels = async () => {},
  setSelectedTab = async (tab: "channels" | "global") => {},
  setSelectedChannel = async (channel: any) => {},
}: EditChannelProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [requiresInvite, setRequiresInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
    variant: "filled" | "outlined" | "standard";
  }>({
    open: false,
    message: "",
    severity: "success",
    variant: "outlined",
  });

  useEffect(() => {
    if (open) {
      setName(initialName);
      setDescription(initialDescription);
      setIsPublic(initialIsPublic);
      setRequiresInvite(initialRequiresInvite);
      setPreviewImage(initialImage || null);
      setWhatsappLink(initialWhatsappLink || "");
    }
  }, [
    initialName,
    initialDescription,
    initialIsPublic,
    initialRequiresInvite,
    open,
    initialImage,
    initialWhatsappLink,
  ]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setIsPublic(false);
    setRequiresInvite(false);
    setSelectedImage(null);
    setWhatsappLink("");
    setPreviewImage(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await ChannelService.updateChannelName({ channelId, name });
      await ChannelService.updateChannelDescription({ channelId, description });
      await ChannelService.updateChannelPrivacy({ channelId, isPublic });
      await ChannelService.updateRequiresInvite({ channelId, requiresInvite });
      await ChannelService.updateWhatsappLink({ channelId, whatsappLink });
      if (selectedImage && previewImage !== initialImage) {
       await ChannelService.updateChannelImage(channelId, selectedImage);
      }
      setSnackbar({
        open: true,
        message: "Canal actualizado exitosamente.",
        severity: "success",
        variant: "standard",
      });
      resetForm();
      onUpdated();
      onClose();
      await fetchChannels();
      await fetchPublicChannels();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al actualizar el canal",
        severity: "error",
        variant: "outlined",
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImage(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleDelete = async () => {
    try {
      setLoading(true);
      await ChannelService.deleteChannel(channelId);
      setSnackbar({
        open: true,
        message: "Canal eliminado exitosamente",
        severity: "success",
        variant: "standard",
      });
      await fetchChannels();
      await fetchPublicChannels();
      setSelectedTab("channels");
      setSelectedChannel(null);
      onClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error al eliminar el canal",
        severity: "error",
        variant: "standard",
      });
    } finally {
      setLoading(false);
    }
  };

  const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: 600,
    maxHeight: "85vh",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setPreviewImage(null);
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={style}
        >
          <Card elevation={3} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Información del canal
              </Typography>

              {/* Imagen */}
              <Box sx={{ mb: 2 }}>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : "divider",
                    borderRadius: "50%",
                    width: 120,
                    height: 120,
                    mx: "auto",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default",
                    cursor: "pointer",
                    transition: "border-color 0.3s ease",
                  }}
                >
                  <input {...getInputProps()} />
                  {previewImage ? (
                    <>
                      <Avatar
                        src={previewImage}
                        alt="Preview"
                        sx={{ width: 112, height: 112 }}
                      />
                      <IconButton
                        onClick={handleRemoveImage}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          bgcolor: "white",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "grey.100" },
                          zIndex: 1,
                        }}
                        disabled={loading}
                      >
                        <Cancel fontSize="small" color="error" />
                      </IconButton>
                    </>
                  ) : (
                    <Box sx={{ textAlign: "center" }}>
                      <CloudUpload
                        sx={{ fontSize: 32, color: "text.secondary", mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Arrastra una imagen o haz clic para subir
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Nombre */}
              <TextField
                label="Nombre del canal"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                size="small"
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              {/* Descripción */}
              <TextField
                label="Descripción"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                multiline
                size="small"
                InputProps={{ sx: { borderRadius: 2 } }}
              />

              <TextField
                label="Enlace de WhatsApp"
                fullWidth
                value={whatsappLink}
                onChange={(e) => setWhatsappLink(e.target.value)}
                margin="normal"
                size="small"
                InputProps={{ sx: { borderRadius: 2 } }}
                placeholder="https://chat.whatsapp.com/..."
              />

              {/* Switches */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    color="primary"
                  />
                }
                label="¿Público?"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={requiresInvite}
                    onChange={(e) => setRequiresInvite(e.target.checked)}
                    color="primary"
                  />
                }
                label="¿Requiere invitación?"
              />
              {/* Botón guardar */}
              <Box sx={{ mt: 3 }}>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  startIcon={<Save />}
                  fullWidth
                  sx={{ borderRadius: 2, fontWeight: 600, py: 1.3, bgcolor: "#308a46", "&:hover": { bgcolor: "#246a35" } }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </Box>
              {/* Botón eliminar */}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setConfirmDeleteOpen(true)}
                  fullWidth
                >
                  Eliminar canal
                </Button>
              </Box>

              {/* Confirmar eliminación */}
              <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                channelName={initialName}
                onClose={() => setConfirmDeleteOpen(false)}
                onConfirm={() => {
                  onClose();
                  handleDelete();
                  setConfirmDeleteOpen(false);
                }}
              />
            </CardContent>
          </Card>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant={snackbar.variant}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
